import { capitalize, createApp } from "vue";
import { API_BASE_URL } from "@/config";
import App from "./App.vue";
import router from "./router";
import store from "./store/index";
import dotenv from "dotenv";

dotenv.config();

const app = createApp(App);
app.use(router);
app.use(store);

const domain = window.location.hostname;
let client = "verify"; // Default client initialization

if (domain.includes("verify")) {
  client = "verify";
} else if (domain.includes("wizzit")) {
  client = "wizzit";
} else if (domain.includes("mobiafrica")) {
  client = "mobiafrica";
} else if (domain.includes("vodacom")) {
  client = "vodacom";
} else if (domain.includes("weelee")) {
  client = "weelee";
} else if (domain.includes("nutun")) {
  client = "nutun";
} else if (domain.includes("nurturelink")) {
  client = "nurturelink";
} else if (domain.includes("suzuki")) {
  client = "suzuki";
} else if (domain.includes("veersgroup")) {
  client = "veersgroup";
} else if (domain.includes("eaglecorner")) {
  client = "eaglecorner";
} else if (domain.includes("hybridconcepts")) {
  client = "hybridconcepts";
} else if (domain.includes("eeb")) {
  client = "eeb";
} else if (domain.includes("miway")) {
  client = "miway";
} else if (domain.includes("mfo")) {
  client = "mfo";
} else if (domain.includes("ittsc")) {
  client = "ittsc";
} else if (domain.includes("smartsure")) {
  client = "smartsure";
} else if (domain.includes("experian")) {
  client = "experian";
} else if (domain.includes("autocity")) {
  client = "autocity";
} else if (domain.includes("yumbi")) {
  client = "yumbi";
} else if (domain.includes("santam")) {
  client = "santam";
} else if (domain.includes("altron")) {
  client = "altron";
} else if (domain.includes("swift")) {
  client = "swift";
} else if (domain.includes("webuycars")) {
  client = "webuycars";
} else if (domain.includes("neutrinori")) {
  client = "neutrinori";
} else if (domain.includes("getsavvi")) {
  client = "getsavvi";
}

// Set client-specific data attribute
if (client) {
  document.documentElement.setAttribute("data-client", client);
}

// Fetch client branding before mounting
async function fetchClientBranding() {
  try {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const response = await fetch(
      `${API_BASE_URL}/verify_api/client/branding/?t=${timestamp}`,
    );
    if (response.ok) {
      const branding = await response.json();
      store.commit("set_branding", branding);
      return branding;
    }
  } catch (error) {
    console.error("Failed to fetch branding");
  }
  return null;
}

const branding = await fetchClientBranding();

const logos = {
  verify: require("@/assets/images/new_black_logo2.svg"),
  wizzit: require("@/assets/images/Wizzit-Logo-Red.png"),
  mobiafrica: require("@/assets/images/mobi_logo.png"),
  vodacom: require("@/assets/images/Vodacom_logo.jpg"),
  weelee: require("@/assets/images/Weelee_logo.png"),
  nutun: require("@/assets/images/Nutun_logo.png"),
  nurturelink: require("@/assets/images/Nurturelink_logo.png"),
  suzuki: require("@/assets/images/Suzuki_logo.png"),
  veersgroup: require("@/assets/images/VeersGroup_logo.jpg"),
  eaglecorner: require("@/assets/images/EagleCorner_logo.png"),
  hybridconcepts: require("@/assets/images/HybridConcepts_logo.png"),
  eeb: require("@/assets/images/EEB_logo.png"),
  miway: require("@/assets/images/MiWay_logo.png"),
  mfo: require("@/assets/images/MFO_logo.png"),
  ittsc: require("@/assets/images/ITTSC_logo.png"),
  smartsure: require("@/assets/images/SmartSure_logo.png"),
  experian: require("@/assets/images/Experian_logo.png"),
  autocity: require("@/assets/images/AutoCity_logo.png"),
  yumbi: require("@/assets/images/Yumbi_logo.png"),
  santam: require("@/assets/images/Santam_logo.png"),
  altron: require("@/assets/images/Altron_logo.png"),
  swift: require("@/assets/images/swift-financial-logo.png"),
  webuycars: require("@/assets/images/WeBuyCars_logo.png"),
  neutrinori: require("@/assets/images/neutrinori_logo_color.png"),
  getsavvi: require("@/assets/images/getsavvi-logo.png"),
};

// Override with branding data
const defaultBranding = {
  primary_color: "#59FF74",
  secondary_color: "#212529",
  logo_width: "100px",
  client_text: "dark",
  primary_color_rgb: "rgb(89, 255, 116)",
  secondary_color_rgb: "rgb(33, 37, 41)",
  icon_filter: "",
  table_header_bg: "#59FF74",
  sidebar_bg: "",
  sidebar_theme: "dark",
  sidebar_text_color: "",
  sidebar_link_color: "",
  sidebar_active_bg: "#59FF74",
  sidebar_hover_bg: "#59FF74",
  welcome_text: "",
  subtext: "Your trusted partner for verification and compliance",
  button_text: "Get Started",
  footer_text: "© 2024 TGPDC. All rights reserved.",
  app_footer_text: null,
  landing_url: null,
  logo_url: null,
  footer_logo_url: null,
};

const currentBranding = branding || defaultBranding;

// Set CSS variables for dynamic theming
document.documentElement.style.setProperty(
  "--primary-color",
  currentBranding.primary_color,
);
document.documentElement.style.setProperty(
  "--secondary-color",
  currentBranding.secondary_color || "#212529",
);
document.documentElement.style.setProperty(
  "--client-text",
  currentBranding.client_text === "light" ? "#f8f9fa" : "#212529",
);
document.documentElement.style.setProperty(
  "--logo-width",
  currentBranding.logo_width,
);
document.documentElement.style.setProperty(
  "--table-header-bg",
  currentBranding.table_header_bg || currentBranding.primary_color,
);

// Set icon filter if provided from branding
// For best color accuracy, use https://codepen.io/sosuke/pen/Pjoqqp to generate the filter
if (currentBranding.icon_filter) {
  document.documentElement.style.setProperty(
    "--icon-filter-primary",
    currentBranding.icon_filter,
  );
} else {
  // No filter - icons will show as-is (black by default)
  // Consider setting icon_filter in Django admin for proper primary color display
  document.documentElement.style.setProperty("--icon-filter-primary", "none");
}

// Set sidebar background based on theme
// Only use sidebar_bg when theme is "custom"
if (currentBranding.sidebar_theme === "custom" && currentBranding.sidebar_bg) {
  document.documentElement.style.setProperty(
    "--sidebar-bg",
    currentBranding.sidebar_bg,
  );
} else if (currentBranding.sidebar_theme === "light") {
  document.documentElement.style.setProperty("--sidebar-bg", "#ffffff");
} else {
  // Dark theme default
  document.documentElement.style.setProperty("--sidebar-bg", "#212529");
}

// Set sidebar theme attribute for CSS selectors
document.documentElement.setAttribute(
  "data-sidebar-theme",
  currentBranding.sidebar_theme || "dark",
);

// Set sidebar text color (theme-aware defaults)
const sidebarTextColor =
  currentBranding.sidebar_text_color ||
  (currentBranding.sidebar_theme === "light" ? "#212529" : "#ECF0F1");
document.documentElement.style.setProperty(
  "--sidebar-text-color",
  sidebarTextColor,
);

// Set sidebar link color (theme-aware defaults)
const sidebarLinkColor =
  currentBranding.sidebar_link_color ||
  (currentBranding.sidebar_theme === "light" ? "#495057" : "#8b92a9");
document.documentElement.style.setProperty(
  "--sidebar-link-color",
  sidebarLinkColor,
);

// Set sidebar active background (defaults to primary color)
document.documentElement.style.setProperty(
  "--sidebar-active-bg",
  currentBranding.sidebar_active_bg || currentBranding.primary_color,
);

// Set sidebar hover background (defaults to primary color)
document.documentElement.style.setProperty(
  "--sidebar-hover-bg",
  currentBranding.sidebar_hover_bg || currentBranding.primary_color,
);

app.config.globalProperties.$client = capitalize(client);

app.config.globalProperties.$clientLogo =
  currentBranding.logo_url ||
  logos[client] ||
  require("@/assets/images/new_black_logo2.png");

app.config.globalProperties.$clientSideBarIcon =
  client === "verify" || client === "mobiafrica"
    ? {
        Dashboard: require("@/assets/images/green_layers.png"),
        Services: require("@/assets/images/green_services-icon.png"),
        Assets: require("@/assets/images/green_form_management.png"),
        Questions: require("@/assets/images/green_questions.png"),
        Forms: require("@/assets/images/green_form_management.png"),
        Templates: require("@/assets/images/green_template_management.png"),
        // Add all other green icons here
      }
    : client === "wizzit" ||
      client === "vodacom" ||
      client === "weelee" ||
      client === "nutun" ||
      client === "nurturelink" ||
      client === "suzuki" ||
      client === "veersgroup" ||
      client === "eaglecorner" ||
      client === "hybridconcepts" ||
      client === "eeb" ||
      client === "miway" ||
      client === "mfo" ||
      client === "ittsc" ||
      client === "smartsure" ||
      client === "experian" ||
      client === "autocity" ||
      client === "yumbi" ||
      client === "santam" ||
      client === "altron" ||
      client === "swift" ||
      client === "webuycars" ||
      client === "neutrinori" ||
      client === "getsavvi"
    ? {
        Dashboard: require("@/assets/images/layers.png"),
        Services: require("@/assets/images/services-icon.png"),
        Assets: require("@/assets/images/form_management.png"),
        Questions: require("@/assets/images/questions.png"),
        Forms: require("@/assets/images/form_management.png"),
        Templates: require("@/assets/images/template_management.png"),
        // Add all other red icons here
      }
    : {};

app.config.globalProperties.$clientServiceIcon = {
  AvailableServices: require("@/assets/side_bar_icons/services.svg"),
};

app.config.globalProperties.$clientImage =
  client === "verify" || client === "mobiafrica"
    ? {
        ThankYou: require("@/assets/images/workflow_done.png"),
        // Add all other green icons here
      }
    : client === "wizzit" ||
      client === "vodacom" ||
      client === "weelee" ||
      client === "nutun" ||
      client === "nurturelink" ||
      client === "suzuki" ||
      client === "veersgroup" ||
      client === "eaglecorner" ||
      client === "hybridconcepts" ||
      client === "eeb" ||
      client === "miway" ||
      client === "mfo" ||
      client === "ittsc" ||
      client === "smartsure" ||
      client === "experian" ||
      client === "autocity" ||
      client === "yumbi" ||
      client === "santam" ||
      client === "altron" ||
      client === "swift" ||
      client === "webuycars" ||
      client === "neutrinori" ||
      client === "getsavvi"
    ? {
        ThankYou: require("@/assets/images/red_workflow.png"),
        // Add all other red icons here
      }
    : {};

app.mount("#app");
